import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const TEXT = {
  ar: {
    subtitle: 'إنشاء حساب',
    name: 'الاسم الكامل',
    email: 'البريد الإلكتروني',
    password: 'كلمة المرور',
    confirm: 'تأكيد كلمة المرور',
    submit: 'إنشاء الحساب',
    submitting: 'جاري الإنشاء...',
    login: 'لديك حساب؟ سجّل الدخول',
    mismatch: 'كلمتا المرور غير متطابقتين',
    short: 'يجب أن تتكون كلمة المرور من 8 أحرف على الأقل',
    done: 'تم إنشاء حسابك. سيقوم المسؤول بتفعيل صلاحياتك، ثم يمكنك تسجيل الدخول.',
  },
  en: {
    subtitle: 'Create an account',
    name: 'Full name',
    email: 'Email',
    password: 'Password',
    confirm: 'Confirm password',
    submit: 'Create account',
    submitting: 'Creating...',
    login: 'Already have an account? Sign in',
    mismatch: 'Passwords do not match',
    short: 'Password must be at least 8 characters',
    done: 'Your account was created. An administrator will activate your access, then you can sign in.',
  },
};

// New accounts start without access; an admin gives them the tech or admin
// role from the Techs page.
const Register = () => {
  const { lang = 'ar' } = useParams();
  const t = TEXT[lang] || TEXT.ar;
  const navigate = useNavigate();
  const { register, logout } = useAuth();
  const [form, setForm] = useState({ name: '', email: '', password: '', confirm: '' });
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirm) return setError(t.mismatch);
    if (form.password.length < 8) return setError(t.short);
    setSubmitting(true);
    try {
      await register(form.email, form.password, form.name.trim());
      await logout();
      setMessage(t.done);
      setTimeout(() => navigate(`/${lang}/login`), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const input = (id, type, autoComplete) => (
    <div>
      <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor={id}>{t[id]}</label>
      <input
        id={id}
        type={type}
        autoComplete={autoComplete}
        dir={type === 'text' ? undefined : 'ltr'}
        value={form[id]}
        onChange={set(id)}
        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
        required
      />
    </div>
  );

  return (
    <div className="flex justify-center items-center min-h-screen bg-gray-100 p-4" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold">البروفيسور للفحص الفني للمركبات</h1>
          <h2 className="text-xl" dir="ltr">ALPROFESSOR VEHICLES TECHNICAL TESTING</h2>
          <p className="mt-2 text-gray-600">{t.subtitle}</p>
        </div>

        {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">{error}</div>}
        {message && <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">{message}</div>}

        <form onSubmit={handleSubmit} className="space-y-4">
          {input('name', 'text', 'name')}
          {input('email', 'email', 'username')}
          {input('password', 'password', 'new-password')}
          {input('confirm', 'password', 'new-password')}
          <button
            type="submit"
            disabled={submitting || !!message}
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-bold py-2 px-4 rounded w-full"
          >
            {submitting ? t.submitting : t.submit}
          </button>
          <div className="text-center">
            <Link to={`/${lang}/login`} className="text-blue-600 hover:text-blue-800 text-sm">{t.login}</Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Register;
